import React, { useState, useEffect } from "react";
import { Modal, Button, Select, Card, Checkbox, Row, Col, Spin, Tag, Space } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { NewAnalysisRequest } from "../../../../../api/labiq/SampleApi";
import { fetchTestCategories } from "../../../../../api/healthcare/Testing/TestCategories";
import { fetchTestTypesByCategory } from "../../../../../api/healthcare/Testing/TestTypes";
import { Sample } from "../../../../../types/lab/SampleTypes";
import { TestCategory, TestType } from "../../../../../types/healthcare/TestTypes";

interface AnalysisModalProps {
	isOpen: boolean;
	onClose: (sample?: Sample) => void;
	sample: Sample;
}

const AnalysisModal: React.FC<AnalysisModalProps> = ({ isOpen, onClose, sample }) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [selectedAnalyses, setSelectedAnalyses] = useState<string[]>([]);
	const [testCategories, setTestCategories] = useState<TestCategory[]>([]);
	const [testTypes, setTestTypes] = useState<TestType[]>([]);
	const [selectedCategory, setSelectedCategory] = useState<string>("");

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const response = await fetchTestCategories({
					include_test_types: false,
				});
				if (response.success) {
					setTestCategories(response.data.filter((cat) => cat.is_active));
				}
			} catch (err) {
				console.error("Error fetching test categories:", err);
			}
		};
		fetchCategories();
	}, []);

	useEffect(() => {
		if (selectedCategory) {
			const fetchTestTypesForCategory = async () => {
				try {
					const response = await fetchTestTypesByCategory(selectedCategory, 1);
					if (response.success) {
						setTestTypes(response.data.filter((type) => type.is_active));
					}
				} catch (err) {
					console.error("Error fetching test types:", err);
				}
			};
			fetchTestTypesForCategory();
		} else {
			setTestTypes([]);
		}
	}, [selectedCategory]);

	const toggleAnalysisSelection = (analysisName: string) => {
		setSelectedAnalyses((prev) => {
			if (prev.includes(analysisName)) {
				return prev.filter((item) => item !== analysisName);
			} else {
				return [...prev, analysisName];
			}
		});
	};

	const handleCategoryChange = (categoryValue: string) => {
		setSelectedCategory(categoryValue);
		setSelectedAnalyses([]);
	};

	const handleSubmit = async () => {
		if (selectedAnalyses.length === 0) {
			return;
		}

		setIsLoading(true);
		try {
			const updatedSample = await NewAnalysisRequest(sample.id as number, selectedAnalyses);
			onClose(updatedSample);
		} catch (err) {
			console.error("Error requesting analysis:", err);
			onClose();
		} finally {
			setIsLoading(false);
		}
	};

	const categoryOptions = testCategories.map((category) => ({
		label: category.name,
		value: category.value,
	}));

	return (
		<Modal
			open={isOpen}
			onCancel={() => onClose()}
			title="Request Additional Analysis"
			width="50rem"
			style={{ maxHeight: "80vh" }}
			footer={
				<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
					<Button icon={<CloseOutlined />} onClick={() => onClose()} size="small">
						Cancel
					</Button>
					<Button type="primary" onClick={handleSubmit} disabled={selectedAnalyses.length === 0 || isLoading} loading={isLoading} size="small">
						Submit
					</Button>
				</div>
			}
		>
			{isLoading ? (
				<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
					<Spin size="large" />
				</div>
			) : (
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					{/* Category Selection */}
					<Card title="Select Test Category" size="small">
						<Select
							value={selectedCategory || null}
							options={categoryOptions}
							onChange={handleCategoryChange}
							placeholder="Select a test category"
							showSearch
							filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
							size="small"
							style={{ width: "100%" }}
						/>
					</Card>

					{/* Test Type Selection */}
					{selectedCategory && testTypes.length > 0 && (
						<Card title="Available Tests" size="small">
							<Row gutter={[12, 12]}>
								{testTypes.map((testType) => (
									<Col xs={24} sm={12} key={testType.id}>
										<Checkbox checked={selectedAnalyses.includes(testType.name)} onChange={() => toggleAnalysisSelection(testType.name)}>
											<div>
												<div>{testType.name}</div>
												{testType.description && <div style={{ fontSize: "11px", color: "var(--text-color-secondary)", marginTop: 2 }}>{testType.description}</div>}
											</div>
										</Checkbox>
									</Col>
								))}
							</Row>
						</Card>
					)}

					{/* No tests message */}
					{selectedCategory && testTypes.length === 0 && (
						<Card size="small">
							<div style={{ textAlign: "center", padding: 16 }}>
								<p style={{ color: "var(--text-color-secondary)" }}>No tests available for the selected category.</p>
							</div>
						</Card>
					)}

					{/* Selected analyses summary */}
					{selectedAnalyses.length > 0 && (
						<Card title="Selected Analyses" size="small">
							<Space size={[8, 8]} wrap>
								{selectedAnalyses.map((analysis, index) => (
									<Tag key={index} color="processing" closable onClose={() => toggleAnalysisSelection(analysis)}>
										{analysis}
									</Tag>
								))}
							</Space>
						</Card>
					)}
				</div>
			)}
		</Modal>
	);
};

export default AnalysisModal;
